import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { Box, Link, Stack, Typography } from '@mui/material';

export interface DeliveryHistoryEntry {
  touchpointId: string;
  channel: string;
  status: string;
  /** Already formatted for reading; undefined when nothing has a timestamp. */
  timestamp?: string;
  /** Absolute ISO value, for the title attribute. */
  timestampIso?: string;
  /** The secure link lifted out of the payload so a grader can open it. */
  secureLink?: string;
  /** The full payload, rendered verbatim — AC-20 asks for the message as sent. */
  payload?: Record<string, unknown>;
}

export interface DeliveryHistoryListProps {
  entries: DeliveryHistoryEntry[];
}

/**
 * AC-20: every sent request message as it was sent — channel, timestamp,
 * delivery status, and the full payload with the secure link. This is the
 * acceptance grader's surface, so the payload is shown verbatim rather than
 * summarised, and the link is a real anchor the grader can follow.
 */
export const DeliveryHistoryList = ({ entries }: DeliveryHistoryListProps) => {
  if (entries.length === 0) {
    return (
      <Typography sx={{ fontSize: 12.5, color: ELIG_TOKENS.inkSoft }}>
        Nothing has been sent yet.
      </Typography>
    );
  }

  return (
    <Box
      component="ul"
      aria-label="Delivery history"
      sx={{
        listStyle: 'none',
        m: 0,
        pl: 1.5,
        borderLeft: `2px solid ${ELIG_TOKENS.line}`,
      }}
    >
      {entries.map(entry => (
        <Box component="li" key={entry.touchpointId} sx={{ py: 0.75 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Typography
              sx={{ fontSize: 12.5, fontWeight: 700, color: ELIG_TOKENS.ink }}
              title={entry.timestampIso}
            >
              {entry.channel.toUpperCase()}
              {entry.timestamp ? ` · ${entry.timestamp}` : ''}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: ELIG_TOKENS.inkSoft }}>
              {entry.status}
            </Typography>
          </Stack>

          {entry.secureLink && (
            <Link
              href={entry.secureLink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: 12.5, wordBreak: 'break-all' }}
            >
              {entry.secureLink}
            </Link>
          )}

          {entry.payload && (
            <Box
              component="pre"
              sx={{
                m: 0.5,
                p: 1,
                fontSize: 11.5,
                lineHeight: 1.45,
                overflowX: 'auto',
                backgroundColor: ELIG_TOKENS.bg,
                border: `1px solid ${ELIG_TOKENS.line}`,
                borderRadius: 1,
              }}
            >
              {JSON.stringify(entry.payload, null, 2)}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};
